import { ONE_TIME_CADENCES, PaymentPlanOption, PlanCadence } from '../services/api/paymentApi';

const getByPath = (source: any, path: string): any =>
  path
    .split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), source);

const LIST_KEYS = ['plans', 'subscriptions', 'data', 'items', 'results'];

/** Finds the array in a response: the given dot path, else the usual wrappers. */
const findList = (response: any, path: string): any[] => {
  if (path.trim()) {
    const found = getByPath(response, path.trim());
    return Array.isArray(found) ? found : found && typeof found === 'object' ? [found] : [];
  }
  if (Array.isArray(response)) return response;
  for (const key of LIST_KEYS) {
    const value = response?.[key];
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') {
      const nested = findList(value, '');
      if (nested.length) return nested;
      if (Array.isArray(value.plans)) return value.plans;
    }
  }
  return response && typeof response === 'object' && Array.isArray(response.plans) ? response.plans : [];
};

const CADENCE_BY_LOWER = new Map(ONE_TIME_CADENCES.map((c) => [c.toLowerCase(), c]));
const ALIASES: Record<string, PlanCadence> = {
  day: 'Daily', week: 'Weekly', month: 'Monthly', quarter: 'Quarterly',
  'half yearly': 'Half-Yearly', halfyearly: 'Half-Yearly', year: 'Yearly', annual: 'Yearly', annually: 'Yearly',
};

const toCadence = (value: unknown): PlanCadence | null => {
  const key = String(value ?? '').trim().toLowerCase();
  return (CADENCE_BY_LOWER.get(key) as PlanCadence) || ALIASES[key] || null;
};

const str = (value: unknown): string => (value == null ? '' : String(value));

/** Gateway plan ids an API plan carries: finuro's `gatewayPlanIds[]`, or the older per-gateway fields. */
const candidateGatewayPlans = (plan: any): PaymentPlanOption['adoptGatewayPlans'] => {
  const out: Record<string, string> = {};
  (Array.isArray(plan?.gatewayPlanIds) ? plan.gatewayPlanIds : []).forEach((entry: any) => {
    if (entry?.gateway && entry?.planId) out[String(entry.gateway).toLowerCase()] = String(entry.planId);
  });
  if (plan?.cashfreePlanId && !out.cashfree) out.cashfree = String(plan.cashfreePlanId);
  if (plan?.razorpayPlanId && !out.razorpay) out.razorpay = String(plan.razorpayPlanId);
  return out;
};

export interface PlanImportResult {
  plans: PaymentPlanOption[];
  /** Plans in the response that can't be used (disabled, no price, unknown cycle). */
  skipped: number;
}

/**
 * Turns a finuro-style subscriptions response (a list of subscriptions with
 * `plans`, one subscription, or a bare plan list) into plan options.
 */
export const extractPlans = (response: any, path = ''): PlanImportResult => {
  const rows = findList(response, path);
  const flat: { plan: any; parent?: any }[] = [];
  rows.forEach((row) => {
    if (Array.isArray(row?.plans)) row.plans.forEach((plan: any) => flat.push({ plan, parent: row }));
    else flat.push({ plan: row });
  });

  let skipped = 0;
  const plans: PaymentPlanOption[] = [];
  flat.forEach(({ plan, parent }) => {
    const cadence = toCadence(plan?.type ?? plan?.cadence ?? plan?.interval ?? plan?.period);
    const price = Number(plan?.price ?? plan?.amount);
    // Lifetime can only be one-time; otherwise the API says, defaulting to recurring.
    const oneTime = plan?.payment === 'one_time' || cadence === 'Lifetime';
    if (!plan || plan.disabled || !cadence || !(price > 0)) {
      skipped += 1;
      return;
    }
    const sourceId = str(plan._id ?? plan.id);
    const discountMode =
      plan.discountMode === 'flat' ? 'flat' : plan.discountMode === 'pct' || plan.discountMode === 'percentage' ? 'percentage' : 'none';
    const discountValue =
      discountMode === 'flat'
        ? Number(plan.discountFlat ?? plan.discountValue) || 0
        : discountMode === 'percentage'
          ? Number(plan.discountPercent ?? plan.discountValue) || 0
          : 0;
    const title = str(parent?.title || parent?.name);
    plans.push({
      id: '',
      sourceId,
      label: str(plan.name) || [title, cadence].filter(Boolean).join(' - '),
      description: str(plan.description) || undefined,
      price,
      payment: oneTime ? 'one_time' : 'recurring',
      cadence,
      intervalCount: plan.intervalCount > 1 ? plan.intervalCount : undefined,
      discountMode: discountValue > 0 ? discountMode : 'none',
      discountValue: discountValue > 0 ? discountValue : undefined,
      discountType: plan.discountType === 'first_time' ? 'first_time' : 'all_time',
      gstMode: ['included', 'excluded'].includes(plan.gstMode) ? plan.gstMode : 'none',
      isPopular: Boolean(plan.isPopular),
      gatewayPlans: {},
      adoptGatewayPlans: oneTime ? {} : candidateGatewayPlans(plan),
    });
  });
  return { plans, skipped };
};

/** Updates plans already imported from the same source id, appends new ones, leaves manual plans alone. */
export const mergeImportedPlans = (
  existing: PaymentPlanOption[],
  imported: PaymentPlanOption[],
  newId: () => string,
): { plans: PaymentPlanOption[]; added: number; updated: number } => {
  const next = [...existing];
  let added = 0;
  let updated = 0;
  imported.forEach((incoming) => {
    const index = incoming.sourceId ? next.findIndex((p) => p.sourceId === incoming.sourceId) : -1;
    if (index >= 0) {
      const { id, gatewayPlans, gatewaySync } = next[index];
      next[index] = { ...incoming, id, gatewayPlans, gatewaySync };
      updated += 1;
    } else {
      next.push({ ...incoming, id: newId() });
      added += 1;
    }
  });
  return { plans: next, added, updated };
};
